// Turns a save response into something a form can render.
//
// The API already explains itself well. A rejected temperature comes back as:
//
//   422 {
//     error: "Temperature: 98.9 is outside the permitted range 30–45 °C and looks
//             like °F (98.9 °F = 37.2 °C). Re-enter in °C, or declare the unit
//             explicitly.",
//     errors: [ ...same, flat... ],
//     field_errors: [ { path, field, value, unit, rule, reason, message } ],
//     ruleset_version: "2026-07-29.2"
//   }
//
// Every section except Echocardiogram was throwing that away in a catch block that
// only called console.error, so the operator clicked Save and nothing at all
// happened on screen — no message, no modal, no change. They then either clicked
// again or assumed it had saved. This module exists so that cannot happen.
//
// Two rules the callers must honour:
//   1. A 422 means NOTHING was persisted. Keep the user's input on screen; never
//      clear the form or navigate away.
//   2. `path` is the server's dot path (`clinical_measurements.temperature`). Strip
//      the section prefix and key your inputs off the remainder, so the form and the
//      API agree on field identity without a translation table.

/**
 * Normalised shape returned by both helpers below.
 *
 *   fieldErrors  { [relativePath]: { message, reason, rule, value, unit, field } }
 *   warnings     { [relativePath]: message }   — soft ranges, saved but amber
 *   summary      string, safe to show in a banner
 *   unmatched    messages that carried no usable path, so they'd otherwise vanish
 *   rulesetVersion  which ruleset decided, for support conversations
 */

const stripSection = (path, section) => {
    if (!path) return path;
    return section && path.startsWith(`${section}.`) ? path.slice(section.length + 1) : path;
};

/**
 * Map a failed save (any error thrown by axios) into renderable state.
 *
 * @param {Error}  error    the axios error
 * @param {string} section  section name to strip from paths, e.g. 'clinical_measurements'
 */
export const parseSaveError = (error, section) => {
    const response = error && error.response;

    // No response at all: offline, DNS, CORS, aborted. The important part of this
    // message is that the entries were NOT saved — otherwise people close the tab.
    if (!response) {
        return {
            fieldErrors: {},
            warnings: {},
            unmatched: [],
            rulesetVersion: null,
            status: 0,
            summary:
                'Could not reach the server, so nothing was saved. Check your connection and press Save again — your entries are still here.'
        };
    }

    const data = response.data || {};
    const fieldErrors = {};
    const unmatched = [];

    for (const entry of Array.isArray(data.field_errors) ? data.field_errors : []) {
        const key = stripSection(entry.path, section);
        if (!key) {
            if (entry.message) unmatched.push(entry.message);
            continue;
        }
        // First error per field wins; the server orders rejects before anything else.
        if (fieldErrors[key]) continue;
        fieldErrors[key] = {
            message: entry.message,
            reason: entry.reason,
            rule: entry.rule,
            value: entry.value,
            unit: entry.unit,
            field: entry.field
        };
    }

    // Older endpoints, and the completeness gate, send only the flat `errors` list.
    // Those have no path, so they'd disappear entirely if we ignored them.
    if (Object.keys(fieldErrors).length === 0 && Array.isArray(data.errors)) {
        unmatched.push(...data.errors);
    }

    const count = Object.keys(fieldErrors).length;
    const summary = (() => {
        if (response.status === 422 || response.status === 400) {
            if (count > 1) return `${count} fields need correcting before this section can be saved. Nothing has been saved yet.`;
            if (count === 1) return data.error || 'One field needs correcting before this section can be saved.';
            return data.error || 'This section could not be saved.';
        }
        if (response.status === 401) return 'Your session has expired. Sign in again, then press Save.';
        if (response.status === 404) return 'This participant could not be found.';
        if (response.status >= 500) return 'The server encountered an error, so nothing was saved. Try again shortly.';
        return data.error || 'This section could not be saved.';
    })();

    return {
        fieldErrors,
        warnings: {},
        unmatched,
        rulesetVersion: data.ruleset_version || null,
        status: response.status,
        summary
    };
};

/**
 * Map a *successful* save's soft-range findings (R-1.3) into amber field warnings.
 * These saved fine — the value is merely outside the expected range and the site is
 * being asked to confirm it.
 */
export const parseSaveWarnings = (responseData, section) => {
    const warnings = {};
    const list = (responseData && (responseData.warnings || responseData.field_warnings)) || [];
    for (const entry of Array.isArray(list) ? list : []) {
        const key = stripSection(entry.path, section);
        if (!key) continue;
        warnings[key] = entry.detail || entry.message;
    }
    return warnings;
};

/** Convenience for the amber confirm banner. */
export const warningSummary = (warnings) => {
    const n = Object.keys(warnings).length;
    if (n === 0) return '';
    return n === 1
        ? 'Saved. One value is outside the expected range — please confirm the field highlighted in amber.'
        : `Saved. ${n} values are outside the expected range — please confirm the fields highlighted in amber.`;
};

export default parseSaveError;
